/**
 * Magic-byte ("file signature") sniffing. We run the same checks on the web
 * client (fast feedback, no wasted upload) and on the API confirm step
 * (defense-in-depth — a curl bypassing the web can't slip a renamed file
 * through). Keeping the table here in `@keobook/shared` makes drift
 * impossible.
 *
 * Coverage: every mime in ALLOWED_DOCUMENT_MIME. Some types (e.g. `text/csv`)
 * have no magic bytes; we sniff with a content heuristic instead.
 */
/** Either-of: file passes if ANY signature matches. */
const SIGNATURES = {
    'application/pdf': [
        { offset: 0, bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] }, // "%PDF-"
    ],
    'image/png': [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }],
    'image/jpeg': [
        { offset: 0, bytes: [0xff, 0xd8, 0xff] }, // SOI marker
    ],
    'image/webp': [
        // "RIFF...." + "WEBP" at offset 8
        { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
        { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
    ],
    'image/heic': [
        // ISO-BMFF ftyp box at offset 4, brand string ("heic"/"heix"/"mif1"/"msf1") at offset 8.
        { offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] },
    ],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        // .xlsx is a ZIP container
        { offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] },
    ],
    'application/vnd.ms-excel': [
        // legacy .xls — OLE2 compound document
        { offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] },
    ],
};
function matchesSig(bytes, sig) {
    if (bytes.length < sig.offset + sig.bytes.length)
        return false;
    for (let i = 0; i < sig.bytes.length; i++) {
        if (bytes[sig.offset + i] !== sig.bytes[i])
            return false;
    }
    return true;
}
/** Returns true if any magic signature for `mime` matches the byte head. */
function matchesAny(bytes, mime) {
    const sigs = SIGNATURES[mime];
    if (!sigs)
        return false;
    return sigs.every((s) => matchesSig(bytes, s));
}
/**
 * Heuristic for plaintext/CSV files (no magic bytes exist). We accept if the
 * first chunk is valid UTF-8 (or printable ASCII) and contains at least one
 * comma per non-empty line in the sample.
 */
function looksLikeText(bytes) {
    const sample = bytes.slice(0, 4096);
    for (const b of sample) {
        // Forbid null bytes and most C0 control chars (allow \t \n \r).
        if (b === 0x00)
            return false;
        if (b < 0x09 || (b > 0x0d && b < 0x20))
            return false;
    }
    return true;
}
/** Decode an ASCII-safe range of a Uint8Array. Bytes ≥ 0x80 become '?' (we
 * only ever inspect ASCII headers — utility, not full UTF-8 decoding). */
function asciiHead(bytes, max) {
    let out = '';
    const len = Math.min(max, bytes.length);
    for (let i = 0; i < len; i++) {
        const b = bytes[i];
        out += b < 0x80 ? String.fromCharCode(b) : '?';
    }
    return out;
}
function looksLikeCsv(bytes) {
    if (!looksLikeText(bytes))
        return false;
    const text = asciiHead(bytes, 4096);
    const lines = text
        .split(/\r?\n/)
        .filter((l) => l.trim().length > 0)
        .slice(0, 5);
    if (lines.length === 0)
        return false;
    return lines.every((l) => l.includes(','));
}
function looksLikeOfxOrQfx(bytes) {
    if (!looksLikeText(bytes))
        return { ok: false, isQfx: false };
    const head = asciiHead(bytes, 1024).replace(/^\s+/, '');
    // Legacy OFX 1.x SGML — file starts with the OFXHEADER line.
    // OFX 2.x — starts with <?xml and includes <?OFX directive.
    const isLegacyOfx = /^OFXHEADER\s*[:=]/i.test(head);
    const isXmlOfx = head.startsWith('<?xml') && /<\?OFX[\s>]/i.test(head);
    const ok = isLegacyOfx || isXmlOfx;
    const isQfx = ok && /INTU\.BID/.test(head);
    return { ok, isQfx };
}
/**
 * Verify the first `bytes` of a file match the claimed MIME type. Pass a
 * decent-sized chunk (≥ 4 KB) — some heuristics scan further than the first
 * dozen bytes.
 */
export function sniffMime(bytes, claimedMime) {
    // Magic-byte mimes
    if (SIGNATURES[claimedMime]) {
        return matchesAny(bytes, claimedMime)
            ? { ok: true }
            : { ok: false, reason: `File contents do not match the declared ${claimedMime} format.` };
    }
    // Text-based mimes
    if (claimedMime === 'text/plain') {
        return looksLikeText(bytes)
            ? { ok: true }
            : { ok: false, reason: 'File does not look like plain text.' };
    }
    if (claimedMime === 'text/csv') {
        return looksLikeCsv(bytes)
            ? { ok: true }
            : {
                ok: false,
                reason: 'File does not look like a CSV (no comma-separated rows in the first 4 KB).',
            };
    }
    if (claimedMime === 'application/x-ofx') {
        const r = looksLikeOfxOrQfx(bytes);
        return r.ok ? { ok: true } : { ok: false, reason: 'File does not look like an OFX document.' };
    }
    if (claimedMime === 'application/vnd.intu.qfx') {
        const r = looksLikeOfxOrQfx(bytes);
        if (!r.ok)
            return { ok: false, reason: 'File does not look like an OFX/QFX document.' };
        if (!r.isQfx)
            return { ok: false, reason: 'File is OFX but missing the QFX INTU.BID directive.' };
        return { ok: true };
    }
    // Unknown mime — accept rather than guess. Upstream allow-list keeps us safe.
    return { ok: true };
}
/** Suggested chunk size to read for sniffing. */
export const SNIFF_CHUNK_BYTES = 8192;
//# sourceMappingURL=file-validation.js.map